import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectCursorPages } from './pagination';
import { api, unwrap } from './client';
import type { components } from './schema';

export type Organization = components['schemas']['Organization'];
export type OrganizationSummary = components['schemas']['OrganizationSummary'];
export type Member = components['schemas']['Member'];
export type Role = components['schemas']['RoleEnum'];
export type AddMemberBody = components['schemas']['AddMemberRequest'];
export type OrganizationPatch = components['schemas']['PatchedOrganizationRequest'];
export type OrganizationBody = components['schemas']['OrganizationRequest'];

/** All roles, in the order shown in role pickers. */
export const ROLES: Role[] = ['owner', 'manager', 'contributor', 'viewer', 'uploader'];

export async function listOrganizations(): Promise<Organization[]> {
  return collectCursorPages(async (cursor) => unwrap(await api.GET('/api/organizations', { params: { query: { cursor } } })));
}
export async function createOrganization(body: OrganizationBody): Promise<Organization> {
  return unwrap(await api.POST('/api/organizations', { body }));
}
export async function getOrganization(id: string): Promise<Organization> {
  return unwrap(await api.GET('/api/organizations/{public_id}', { params: { path: { public_id: id } } }));
}
export async function updateOrganization(id: string, patch: OrganizationPatch): Promise<Organization> {
  return unwrap(await api.PATCH('/api/organizations/{public_id}', { params: { path: { public_id: id } }, body: patch }));
}
export async function listMembers(id: string): Promise<Member[]> {
  return unwrap(await api.GET('/api/organizations/{public_id}/members', { params: { path: { public_id: id } } }));
}
export async function addMember(id: string, body: AddMemberBody): Promise<Member> {
  return unwrap(await api.POST('/api/organizations/{public_id}/members', { params: { path: { public_id: id } }, body }));
}
export async function changeRole(id: string, userId: string, role: Role): Promise<Member> {
  return unwrap(
    await api.PATCH('/api/organizations/{public_id}/members/{user_public_id}', {
      params: { path: { public_id: id, user_public_id: userId } },
      body: { role },
    }),
  );
}
export async function removeMember(id: string, userId: string): Promise<void> {
  unwrap(
    await api.DELETE('/api/organizations/{public_id}/members/{user_public_id}', {
      params: { path: { public_id: id, user_public_id: userId } },
    }),
  );
}

export function useOrganizations() {
  return useQuery({ queryKey: ['organizations'], queryFn: listOrganizations });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: ['organizations', id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });
}

export function useMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: ['members', orgId],
    queryFn: () => listMembers(orgId!),
    enabled: !!orgId,
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrganizationBody) => createOrganization(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['organizations'] }),
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: OrganizationPatch) => updateOrganization(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['organizations', id] });
    },
  });
}

export function useAddMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddMemberBody) => addMember(orgId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', orgId] }),
  });
}

export function useChangeRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) => changeRole(orgId, userId, role),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', orgId] }),
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeMember(orgId, userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', orgId] }),
  });
}
